import { useCallback, useEffect, useState } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate, useParams, generatePath } from 'react-router-dom';

import { Breadcrumbs } from '#client/components/Breadcrumbs';
import ConnectedAccountInfo from '#client/components/ConnectedAccountInfo';
import { DisableLayer } from '#client/components/DisableLayer';
import { FocusGrid, HubContainerGridStyled, HubMainWrapperStyled } from '#client/components/Grid';
import Notifications from '#client/components/Notifications';
import PaymentContainer from '#client/components/PaymentContainer';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { EMPTY_PAYMENT_DESCRIPTION } from '#client/constants/transaction';
import useBankingPath from '#client/hooks/useBankingPath';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCounterpartyDetails from '#client/hooks/useCounterpartyDetails';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useRedirectToLinkedAccounts from '#client/hooks/useRedirectToLinkedAccounts';
import useScrollTo from '#client/hooks/useScrollTo';
import SharedNavigateModals from '#client/pages/SharedNavigateModals';
import { BreadcrumbWrapStyled } from '#client/pages/SharedStyles';
import { isActiveStatus, isNotRelinkableStatus } from '#client/utils/counterpartyStatus';
import { capitalizeFirstLetter } from '#client/utils/text';

import RelinkBanner from './components/RelinkBanner';

dayjs.extend(utc);

const { isWebView } = WebView;

export default function ConnectedAccountTransactionHistory() {
  const { counterPartyId } = useParams();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { linkedAccountsPath } = useRedirectToLinkedAccounts(businessEntityUuid);
  const { isOpeningModal } = useCheckOpenModal();
  const { isDisable } = useDisableDashboardContext();
  const bankingPath = useBankingPath();
  const { counterPartyData, fetchingCounterparty, counterpartyError } = useCounterpartyDetails({
    counterpartyId: counterPartyId
  });

  const navigate = useNavigate();
  const handleUnlinkConfirm = useCallback(() => {
    navigate(linkedAccountsPath, { replace: true });
  }, [navigate, linkedAccountsPath]);
  const [counterParty, setCounterParty] = useState({
    id: counterPartyId,
    bank: null,
    name: null,
    accountNumber: null,
    status: null,
    accountType: null,
    connectedType: null
  });
  const [counterPartyStatus, setCounterPartyStatus] = useState(counterParty.status);

  useEffect(() => {
    setCounterPartyStatus(() => counterParty.status);
  }, [counterParty.status]);

  useEffect(() => {
    if (counterpartyError) {
      navigate('..', { relative: 'path', replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartyError]);

  useEffect(() => {
    const shouldUpdateFormData = Boolean(
      counterPartyId &&
      counterPartyData &&
      !fetchingCounterparty &&
      counterParty?.accountNumber === null
    );

    if (shouldUpdateFormData) {
      setCounterParty({
        ...counterParty,
        ...counterPartyData
      });
    }
  }, [counterParty, counterPartyData, counterPartyId, fetchingCounterparty]);

  const {
    accountNumber,
    id,
    bank,
    name,
    accountType,
    connectedType,
    fullAccountNumber,
    wire,
    isACHSupported,
    isWireSupported
  } = counterParty;

  if (connectedType === COUNTERPARTY_CONNECTED_TYPE.PAYEE) {
    navigate('..', { relative: 'path', replace: true });
  }

  useScrollTo(0, 0);

  const banners = [
    RelinkBanner({
      isArchived: Boolean(counterPartyStatus) && !isActiveStatus(counterPartyStatus),
      isNotRelinkable: isNotRelinkableStatus(counterPartyStatus),
      callback: setCounterPartyStatus,
      counterPartyId: id
    })
  ];

  const connectedAccountPath = generatePath(`${bankingPath}/connected-accounts/:counterPartyId`, {
    counterPartyId
  });

  return isOpeningModal ? (
    <SharedNavigateModals />
  ) : (
    <HubContainerGridStyled>
      <FocusGrid>
        <HubMainWrapperStyled>
          {!isWebView && (
            <BreadcrumbWrapStyled>
              <Breadcrumbs
                items={[
                  {
                    to: '../../finances',
                    label: 'Finances'
                  },
                  {
                    to: '../dashboard',
                    label: 'Banking'
                  },
                  {
                    to: linkedAccountsPath,
                    label: 'Linked Accounts'
                  },
                  ...(bank && accountType
                    ? [
                        {
                          label: `${bank} - ${capitalizeFirstLetter(accountType ?? '')}`
                        }
                      ]
                    : [])
                ]}
              />
            </BreadcrumbWrapStyled>
          )}
          <Notifications banners={banners} />
          <Box position="relative">
            {isDisable && <DisableLayer aria-label="Disable layer" />}
            <ConnectedAccountInfo
              accountNumber={accountNumber}
              accountType={accountType}
              bank={bank}
              connectedType={connectedType}
              counterPartyStatus={counterPartyStatus}
              entryPageForSendMoney={connectedAccountPath}
              fullAccountNumber={fullAccountNumber}
              id={id}
              isACHSupported={isACHSupported}
              isWireSupported={isWireSupported}
              loading={fetchingCounterparty}
              name={name}
              onUnlinkConfirm={handleUnlinkConfirm}
              wire={wire}
            />
            <PaymentContainer
              counterparty={{
                counterpartyId: counterPartyId,
                bank,
                connectedType,
                accountNumber,
                fullAccountNumber
              }}
              emptyText={EMPTY_PAYMENT_DESCRIPTION.BANK_ACCOUNT}
            />
          </Box>
        </HubMainWrapperStyled>
      </FocusGrid>
    </HubContainerGridStyled>
  );
}
